/**
 * Sync queue for offline field-visit writes.
 *
 * Responsibilities:
 *  • Replay pending writes (patch / sign / submit) against the backend
 *    in FIFO order when connectivity returns.
 *  • Surface live status updates so the UI can show a "Syncing X / Y"
 *    indicator.
 *  • Gracefully no-op when:
 *      – Already syncing (mutex)
 *      – Offline (`navigator.onLine === false`)
 *      – No auth token (the rep logged out)
 *
 * On server 409 (conflict) the offending entry is moved to a "needs-
 * attention" state in the visit cache so the UI can prompt the rep.
 */
import {
  listQueue, removeFromQueue, bumpQueueAttempt, clearQueueForVisit,
  cacheVisit, readCachedVisit,
} from "./offline-db";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const listeners = new Set();
let syncing = false;

function authHeader() {
  const tok = typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

function emit(state) {
  listeners.forEach((cb) => {
    try { cb(state); } catch {/* */}
  });
}

export function onSyncStatus(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

async function runOne(entry) {
  const headers = { "Content-Type": "application/json", ...authHeader() };
  const id = entry.visit_id;
  if (entry.kind === "patch") {
    const r = await fetch(`${API}/field-visits/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(entry.payload),
    });
    if (!r.ok) throw await mkErr(r);
    return await r.json();
  }
  if (entry.kind === "sign") {
    const r = await fetch(`${API}/field-visits/${id}/sign-form`, {
      method: "POST",
      headers,
      body: JSON.stringify(entry.payload),
    });
    if (!r.ok) throw await mkErr(r);
    return await r.json();
  }
  if (entry.kind === "submit") {
    const r = await fetch(`${API}/field-visits/${id}/submit`, {
      method: "POST",
      headers,
      body: JSON.stringify(entry.payload || {}),
    });
    if (!r.ok) throw await mkErr(r);
    return await r.json();
  }
  throw new Error(`unknown queue kind: ${entry.kind}`);
}

async function mkErr(r) {
  let detail = `${r.status} ${r.statusText}`;
  try {
    const j = await r.json();
    if (j?.detail) detail = j.detail;
  } catch {/* */}
  const err = new Error(detail);
  err.status = r.status;
  return err;
}

export async function flushQueue({ source = "manual" } = {}) {
  if (syncing) return { ok: false, reason: "already-syncing" };
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { ok: false, reason: "offline" };
  }
  if (!authHeader().Authorization) return { ok: false, reason: "no-auth" };

  syncing = true;
  const items = await listQueue();
  // Filter out entries that are still in their backoff window. The
  // exponential schedule (10s / 30s / 90s) keeps cellular-edge field
  // reps from hammering the API while connectivity is flapping.
  const nowMs = Date.now();
  const eligible = items.filter((it) => {
    if (!it.next_retry_at) return true;
    return new Date(it.next_retry_at).getTime() <= nowMs;
  });
  emit({ phase: "start", total: eligible.length, source });
  let processed = 0;
  let failed = 0;
  let conflict = null;

  for (const entry of eligible) {
    emit({ phase: "step", processed, total: eligible.length, current: entry });
    try {
      const result = await runOne(entry);
      // For patch/submit, refresh the cached visit doc with the server copy
      if (entry.kind === "patch" && result?.id) {
        await cacheVisit(result);
      }
      if (entry.kind === "submit" && result?.demographics_id) {
        await clearQueueForVisit(entry.visit_id);
      }
      await removeFromQueue(entry.queue_id);
      processed += 1;
    } catch (err) {
      failed += 1;
      // Schedule next retry with exponential backoff: 10s → 30s → 90s.
      // After 3 attempts, the queue holds the entry but flushQueue
      // stops trying — the UI surfaces "Submission failed — tap to
      // retry" so the rep can manually re-run.
      const attempts = (entry.attempts || 0) + 1;
      const backoffSec = attempts >= 3 ? null
        : attempts === 1 ? 10
        : attempts === 2 ? 30
        : 90;
      const nextRetryAt = backoffSec
        ? new Date(Date.now() + backoffSec * 1000).toISOString()
        : null;
      await bumpQueueAttempt(entry.queue_id, err?.message || err, nextRetryAt);
      if (err?.status === 409 && !conflict) {
        conflict = { visit_id: entry.visit_id, kind: entry.kind, message: err.message };
        break;
      }
      if (err?.status === 400 || err?.status === 422) {
        await removeFromQueue(entry.queue_id);
      } else {
        // Transient (5xx, network) — leave in queue, try again next flush.
        break;
      }
    }
  }

  emit({ phase: "done", processed, failed, total: eligible.length, conflict });
  syncing = false;
  return { ok: !conflict, processed, failed, conflict };
}

// ── Wire-up ─────────────────────────────────────────────────────
// Flush when the tab regains connectivity OR the SW pings us.
let bound = false;
export function installSyncListeners() {
  if (bound || typeof window === "undefined") return;
  bound = true;

  const trigger = (source) => () => {
    flushQueue({ source }).catch(() => null);
  };
  window.addEventListener("online", trigger("online-event"));
  // Also flush when the tab becomes visible again
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") trigger("visible")();
  });
  // SW broadcast hook
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event?.data?.type === "AVIDA_SYNC_NOW") trigger("sw")();
    });
  }
  // Periodic safety net (every 60s while tab is open)
  setInterval(() => {
    if (typeof navigator !== "undefined" && navigator.onLine) {
      flushQueue({ source: "interval" }).catch(() => null);
    }
  }, 60_000);
}

/**
 * Resolve a 409 conflict: discard local pending writes for this visit
 * and reload the office's authoritative copy from the server.
 */
export async function resolveConflictDiscardMine(visitId) {
  await clearQueueForVisit(visitId);
  // Refresh from server
  try {
    const r = await fetch(`${API}/field-visits/${visitId}`, {
      headers: authHeader(),
    });
    if (r.ok) {
      const fresh = await r.json();
      await cacheVisit(fresh);
      return fresh;
    }
  } catch {/* */}
  return readCachedVisit(visitId);
}

/**
 * Resolve a 409 conflict the other way: tag the queued items with
 * a force_overwrite flag so the next flush bypasses the conflict
 * check. (Server must honour this — see hc_field_visit.py.)
 */
export async function resolveConflictKeepMine(visitId) {
  const items = await listQueue();
  // We don't have direct table mutation here, so we re-enqueue with the flag.
  // Use the offline-db's bump mechanism instead.
  const db = await import("./offline-db");
  for (const it of items) {
    if (it.visit_id !== visitId) continue;
    it.payload = { ...(it.payload || {}), force_overwrite: true };
    // Re-write the row by removing and re-adding.
    await db.removeFromQueue(it.queue_id);
    await db.enqueueWrite({
      visitId: it.visit_id,
      kind: it.kind,
      payload: it.payload,
      meta: it.meta,
    });
  }
  return flushQueue({ source: "conflict-keep-mine" });
}
